import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { t } from '../i18n';

const API = window.location.origin + '/api';

const Matches = () => {
  const token = localStorage.getItem('token');
  const dealer = JSON.parse(localStorage.getItem('dealer') || '{}');
  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams] = useSearchParams();

  const [matches, setMatches] = useState<any[]>([]);
  const [matchFilter, setMatchFilter] = useState<'all'|'active'|'in_transit'|'completed'>('all');
  const [chatOpen, setChatOpen] = useState<Record<number,boolean>>({});
  const [messages, setMessages] = useState<Record<number,any[]>>({});
  const [newMessage, setNewMessage] = useState<Record<number,string>>({});
  const [addressInputs, setAddressInputs] = useState<Record<number,string>>({});
  const [trackingInputs, setTrackingInputs] = useState<Record<number,string>>({});
  const [disputeInputs, setDisputeInputs] = useState<Record<number,string>>({});
  const [loadingAddress, setLoadingAddress] = useState<Record<number,boolean>>({});
  const [loadingTracking, setLoadingTracking] = useState<Record<number,boolean>>({});
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [ratingGiven, setRatingGiven] = useState<Record<number,number>>({});
  const [ratingsLoaded, setRatingsLoaded] = useState(false);

  useEffect(() => {
    fetchMatches();
    if (searchParams.get('payment') === 'success') {
      setPaymentSuccess(true);
      setTimeout(() => setPaymentSuccess(false), 5000);
      // Webhook may take a moment — refresh after 3 seconds
      setTimeout(() => fetchMatches(), 3000);
      setTimeout(() => fetchMatches(), 6000);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(chatOpen).forEach(matchId => {
        if (chatOpen[Number(matchId)]) fetchMessages(Number(matchId));
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [chatOpen]);

  const fetchMatches = () => {
    axios.get(`${API}/matches`, { headers }).then(res => {
      const m = res.data.matches || [];
      setMatches(m);
      // Load existing ratings
      if (!ratingsLoaded) {
        axios.get(`${API}/ratings/my`, { headers }).then(rRes => {
          const given: Record<number,number> = {};
          (rRes.data.ratings || []).forEach((r: any) => { given[r.match_id] = r.score; });
          setRatingGiven(given);
          setRatingsLoaded(true);
        }).catch(() => {});
      }
    });
  };

  const fetchMessages = (matchId: number) => {
    axios.get(`${API}/messages/${matchId}`, { headers }).then(res => {
      setMessages(prev => ({ ...prev, [matchId]: res.data.messages || [] }));
    });
  };

  const toggleChat = (matchId: number) => {
    const isOpen = !chatOpen[matchId];
    setChatOpen(prev => ({ ...prev, [matchId]: isOpen }));
    if (isOpen) {
      fetchMessages(matchId);
      axios.post(`${API}/messages/${matchId}/read`, {}, { headers }).catch(() => {});
    }
  };

  const sendMessage = async (matchId: number) => {
    const content = newMessage[matchId];
    if (!content?.trim()) return;
    await axios.post(`${API}/messages/${matchId}`, { content }, { headers });
    setNewMessage(prev => ({ ...prev, [matchId]: '' }));
    fetchMessages(matchId);
  };

  const handlePayFee = async (matchId: number) => {
    try {
      const res = await axios.post(`${API}/payments/${matchId}/checkout`, {}, { headers });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err: any) { alert(err.response?.data?.error || 'Payment error'); }
  };

  const handleSaveAddress = async (matchId: number) => {
    const address = addressInputs[matchId];
    if (!address) return;
    setLoadingAddress(prev => ({ ...prev, [matchId]: true }));
    await axios.post(`${API}/tracking/${matchId}/address`, { address }, { headers });
    setLoadingAddress(prev => ({ ...prev, [matchId]: false }));
    fetchMatches();
  };

  const handleTracking = async (matchId: number) => {
    const tracking = trackingInputs[matchId];
    if (!tracking) return;
    setLoadingTracking(prev => ({ ...prev, [matchId]: true }));
    await axios.post(`${API}/tracking/${matchId}/tracking`, { tracking }, { headers });
    setLoadingTracking(prev => ({ ...prev, [matchId]: false }));
    fetchMatches();
  };

  const handleConfirm = async (matchId: number) => {
    await axios.post(`${API}/tracking/${matchId}/confirm`, {}, { headers });
    fetchMatches();
  };

  const handleDispute = async (matchId: number) => {
    const reason = disputeInputs[matchId];
    if (!reason) return;
    await axios.post(`${API}/tracking/${matchId}/dispute`, { reason }, { headers });
    fetchMatches();
  };

  const handleRate = async (matchId: number, score: number, toId: number) => {
    await axios.post(`${API}/ratings/${matchId}`, { score, to_dealer_id: toId }, { headers });
    setRatingGiven(prev => ({ ...prev, [matchId]: score }));
  };

  const getDeadlineDays = (deadline: string) => {
    if (!deadline) return null;
    const diff = new Date(deadline).getTime() - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const getStatusColor = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-700';
    if (status === 'disputed') return 'bg-red-100 text-red-700';
    if (status === 'in_transit') return 'bg-blue-100 text-blue-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const filtered = matches.filter(m => {
    if (matchFilter === 'active') return m.status === 'pending';
    if (matchFilter === 'in_transit') return m.status === 'in_transit';
    if (matchFilter === 'completed') return m.status === 'completed';
    return true;
  });

  return (
    <div>
      {paymentSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4 text-green-700 text-sm font-medium">
          ✓ Payment received! Waiting for counterparty to pay their fee.
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('matches','Matches')}</h1>

      <div className="flex gap-2 mb-6">
        {([['all','All'],['active','Active'],['in_transit','In Transit'],['completed','Completed']] as const).map(([f,label]) => (
          <button key={f} onClick={() => setMatchFilter(f as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${matchFilter===f?'bg-gray-900 text-white border-gray-900':'border-gray-200 text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
          <p className="text-gray-400 text-sm">No matches found</p>
        </div>
      )}

      <div className="space-y-4">
        {filtered.map(m => {
          const iAmA = Number(m.dealer_a_id) === Number(dealer.id);
          const myFeePaid = iAmA ? m.fee_a_paid : m.fee_b_paid;
          const theirFeePaid = iAmA ? m.fee_b_paid : m.fee_a_paid;
          const bothPaid = m.fee_a_paid && m.fee_b_paid;
          const myAddress = iAmA ? m.shipping_address_a : m.shipping_address_b;
          const theirAddress = iAmA ? m.shipping_address_b : m.shipping_address_a;
          const counterpartyName = bothPaid ? (iAmA ? m.name_b : m.name_a) : '—';
          const counterpartyEmail = bothPaid ? (iAmA ? m.email_b : m.email_a) : null;
          const counterpartyPhone = bothPaid ? (iAmA ? m.phone_b : m.phone_a) : null;
          const counterpartyId = iAmA ? m.dealer_b_id : m.dealer_a_id;
          const myTracking = iAmA ? m.tracking_a : m.tracking_b;
          const myConfirmed = iAmA ? m.confirmed_a : m.confirmed_b;
          const deadlineDays = getDeadlineDays(m.deadline);

          return (
            <div key={m.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {(m.have_items||[]).map((i:any)=>`${i.quantity}x ${i.product_name}`).join(', ') || `${m.qty_a}x ${m.product_a_name}`}
                      <span className="text-gray-400 mx-2">↔</span>
                      {(m.want_items||[]).map((i:any)=>`${i.quantity}x ${i.product_name}`).join(', ') || `${m.qty_b}x ${m.product_b_name}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Match #{m.id} · {new Date(m.created_at).toLocaleDateString()}
                      {m.status==='pending' && deadlineDays !== null && (
                        <span className={`ml-2 font-medium ${deadlineDays<=1?'text-red-500':'text-gray-400'}`}>
                          · {deadlineDays}d to pay
                        </span>
                      )}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(m.status)}`}>
                    {m.status?.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Counterparty */}
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <p className="text-xs text-gray-400 mb-1">Counterparty</p>
                <p className="text-sm font-medium text-gray-900">{counterpartyName}</p>
                {counterpartyEmail && <a href={`mailto:${counterpartyEmail}`} className="text-xs" style={{color:'#B8860B'}}>{counterpartyEmail}</a>}
                {counterpartyPhone && <p className="text-xs text-gray-500 mt-0.5">📞 {counterpartyPhone}</p>}
                {bothPaid && theirAddress && (
                  <div className="mt-2 text-xs text-gray-600">
                    <p className="font-medium text-gray-700">📦 Ship to:</p>
                    <p className="whitespace-pre-line">{theirAddress}</p>
                  </div>
                )}
              </div>

              {/* Fee status */}
              <div className="px-5 py-3 border-b border-gray-100">
                <div className="flex gap-4 mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${myFeePaid?'bg-green-500':'bg-gray-300'}`}/>
                    <span className="text-xs text-gray-500">My fee {myFeePaid?'paid':'pending'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${theirFeePaid?'bg-green-500':'bg-gray-300'}`}/>
                    <span className="text-xs text-gray-500">Their fee {theirFeePaid?'paid':'pending'}</span>
                  </div>
                </div>

                {/* Step 1: Pay fee */}
                {!myFeePaid && m.status !== 'completed' && !m.disputed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                    <p className="text-xs font-medium text-yellow-800 mb-2">💳 Step 1: Pay match fee (0.1%)</p>
                    <button onClick={() => handlePayFee(m.id)}
                      className="px-4 py-2 rounded-lg text-white text-xs font-semibold"
                      style={{backgroundColor:'#B8860B'}}>
                      {t('payFee','Pay Fee')}
                    </button>
                  </div>
                )}

                {/* My fee paid, waiting for theirs */}
                {myFeePaid && !theirFeePaid && m.status !== 'completed' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                    <p className="text-xs text-blue-700">✓ Your fee paid. Waiting for counterparty to pay...</p>
                  </div>
                )}

                {/* Step 2: Shipping address */}
                {bothPaid && !myAddress && m.status !== 'completed' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                    <p className="text-xs font-medium text-orange-800 mb-2">📦 Step 2: Enter your shipping address</p>
                    <textarea
                      value={addressInputs[m.id] || ''}
                      onChange={e => setAddressInputs(prev => ({...prev,[m.id]:e.target.value}))}
                      rows={3}
                      className="w-full border border-orange-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none mb-2 resize-none"
                      placeholder="Street, City, Postal Code, Country" />
                    <button onClick={() => handleSaveAddress(m.id)} disabled={loadingAddress[m.id]}
                      className="px-4 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                      style={{backgroundColor:'#1a1a2e'}}>
                      {loadingAddress[m.id] ? '...' : 'Save Address'}
                    </button>
                  </div>
                )}

                {/* Step 3: Tracking */}
                {bothPaid && myAddress && !myTracking && m.status !== 'completed' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                    <p className="text-xs font-medium text-orange-800 mb-2">📮 Step 3: Upload tracking number</p>
                    <div className="flex gap-2">
                      <input type="text"
                        value={trackingInputs[m.id] || ''}
                        onChange={e => setTrackingInputs(prev => ({...prev,[m.id]:e.target.value}))}
                        className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none"
                        placeholder="Tracking number..." />
                      <button onClick={() => handleTracking(m.id)} disabled={loadingTracking[m.id]}
                        className="px-3 py-2 rounded-lg text-white text-xs font-semibold"
                        style={{backgroundColor:'#B8860B'}}>
                        {loadingTracking[m.id] ? '...' : 'Upload'}
                      </button>
                    </div>
                  </div>
                )}

                {myTracking && !myConfirmed && m.status !== 'completed' && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-2">
                    <p className="text-xs text-blue-600 mb-2">✓ Tracking uploaded. Waiting for counterparty...</p>
                    <button onClick={() => handleConfirm(m.id)}
                      className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium">
                      ✓ Confirm Receipt
                    </button>
                  </div>
                )}

                {/* Completed */}
                {m.status === 'completed' && (
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-2">
                    <p className="text-xs text-green-700 font-medium mb-2">🎉 Swap completed!</p>
                    {!ratingGiven[m.id] && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Rate counterparty:</p>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(s => (
                            <button key={s} onClick={() => handleRate(m.id, s, counterpartyId)}
                              className="text-xl hover:scale-110 transition-transform">⭐</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {ratingGiven[m.id] && <p className="text-xs text-green-600">✓ Rated {ratingGiven[m.id]}/5</p>}
                  </div>
                )}

                {/* Dispute */}
                {bothPaid && !m.disputed && m.status !== 'completed' && (
                  <div className="flex gap-2 mt-2">
                    <input type="text"
                      value={disputeInputs[m.id] || ''}
                      onChange={e => setDisputeInputs(prev => ({...prev,[m.id]:e.target.value}))}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none"
                      placeholder="Dispute reason..." />
                    <button onClick={() => handleDispute(m.id)}
                      className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium">
                      {t('openDispute','Open Dispute')}
                    </button>
                  </div>
                )}
                {m.disputed && (
                  <p className="text-xs text-red-500 mt-1">⚠️ Dispute opened — admin will contact you within 14 days</p>
                )}
              </div>

              {/* Chat */}
              {bothPaid && (
                <div className="px-5 py-3">
                  <button onClick={() => toggleChat(m.id)}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    💬 Chat with counterparty
                    {messages[m.id]?.length ? ` (${messages[m.id].length})` : ''}
                  </button>

                  {chatOpen[m.id] && (
                    <div className="mt-3">
                      <div className="h-48 overflow-y-auto space-y-2 mb-3 border border-gray-100 rounded-lg p-2">
                        {(messages[m.id] || []).length === 0 && (
                          <p className="text-xs text-gray-300 text-center py-4">No messages yet</p>
                        )}
                        {(messages[m.id] || []).map(msg => (
                          <div key={msg.id} className={`flex ${msg.dealer_id===dealer.id?'justify-end':'justify-start'}`}>
                            <div className={`max-w-xs px-3 py-2 rounded-lg text-xs ${msg.dealer_id===dealer.id?'bg-gray-900 text-white':'bg-gray-100 text-gray-900'}`}>
                              <p className="font-medium mb-0.5 opacity-70">{msg.company_name}</p>
                              {msg.content.includes('||FILE:') ? (
                                <a href={msg.content.split('||FILE:')[1]} target="_blank" rel="noreferrer"
                                  className="underline flex items-center gap-1">
                                  <span>📎</span>{msg.content.split('||FILE:')[0].replace('📎 ','')}
                                </a>
                              ) : <p>{msg.content}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            value={newMessage[m.id]||''}
                            onChange={e => setNewMessage(prev => ({...prev,[m.id]:e.target.value}))}
                            onKeyDown={e => e.key==='Enter' && sendMessage(m.id)}
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none"
                            placeholder="Type a message..." />
                          <button onClick={() => sendMessage(m.id)}
                            className="px-3 py-2 rounded-lg text-white text-xs font-medium"
                            style={{backgroundColor:'#1a1a2e'}}>
                            Send
                          </button>
                        </div>
                        <label className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                          <span>📎 Upload file</span>
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={async(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                await axios.post(`${API}/uploads/${m.id}`, formData, {
                                  headers:{Authorization:`Bearer ${token}`,'Content-Type':'multipart/form-data'}
                                });
                                fetchMessages(m.id);
                              } catch(err:any){ alert(err.response?.data?.error||'Upload failed'); }
                            }} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Matches;
